import React, {useEffect, useId, useMemo, useRef, useState} from 'react';
import {
    Box,
    IconButton,
    InputAdornment,
    Popover,
    Stack,
    TextField,
    TextFieldProps,
    Tooltip,
    Typography
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {Virtuoso, VirtuosoHandle} from 'react-virtuoso';
import {emojiCategories, EmojiOption, emojiWithSkinTone, SkinTone} from './EmojiOptions';
import EmojiPickerToolbar from './EmojiPickerToolbar';

type EmojiRow = {categoryId: string} & ({name: string} | {emojis: EmojiOption[]});
const columns = 11;
const rowHeight = 44;

export default function EmojiPicker({value, onChange, clearable = false, ...props}: {
    value?: string | null,
    onChange: (emoji: string) => void,
    clearable?: boolean,
} & Omit<TextFieldProps, 'onChange' | 'value' | 'clearable'>) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [search, setSearch] = useState('');
    const [skinTone, setSkinTone] = useState<SkinTone>(0);
    const [activeCategory, setActiveCategory] = useState<string>();
    const [categoryJump, setCategoryJump] = useState<string | null>(null);
    const listRef = useRef<VirtuosoHandle>(null);
    const popoverId = useId();
    const open = Boolean(anchorEl) && !props.disabled;
    const rows = useMemo(() => {
        const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const result: EmojiRow[] = [];
        for (const category of emojiCategories) {
            const emojis = category.emojis.map(emoji => emojiWithSkinTone(emoji, skinTone))
                .filter(option => terms.every(term => option.searchText.includes(term)));
            if (!emojis.length) continue;
            result.push({categoryId: category.id, name: category.name});
            for (let index = 0; index < emojis.length; index += columns) {
                result.push({categoryId: category.id, emojis: emojis.slice(index, index + columns)});
            }
        }
        return result;
    }, [search, skinTone]);

    useEffect(() => {
        if (categoryJump === null) return;
        const index = rows.findIndex(row => row.categoryId === categoryJump);
        if (index >= 0) {
            listRef.current?.scrollToIndex({index, align: 'start'});
            setActiveCategory(categoryJump);
        }
        setCategoryJump(null);
    }, [categoryJump, rows]);

    const handleOpen = (element: HTMLElement) => {
        setSearch('');
        setActiveCategory(emojiCategories[0]?.id);
        setAnchorEl(element);
    };
    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <TextField
                {...props}
                value={value || ''}
                onChange={event => onChange(event.target.value)}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                {clearable && value && !props.disabled && (
                                    <IconButton size="small" aria-label="Clear emoji" onClick={() => onChange('')}>
                                        <ClearIcon fontSize="small"/>
                                    </IconButton>
                                )}
                                <Tooltip title="Choose emoji">
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label="Choose emoji"
                                            aria-haspopup="dialog"
                                            aria-expanded={open}
                                            aria-controls={open ? popoverId : undefined}
                                            disabled={props.disabled}
                                            onClick={event => handleOpen(event.currentTarget)}
                                        >
                                            <EmojiEmotionsOutlinedIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                slotProps={{paper: {sx: {width: 432, maxWidth: 'calc(100vw - 32px)'}}}}
            >
                <Stack id={popoverId} role="dialog" aria-label="Choose emoji" gap={2} sx={{p: 2}}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Search emojis"
                        value={search}
                        onChange={event => {
                            setSearch(event.target.value);
                            listRef.current?.scrollTo({top: 0});
                        }}
                        slotProps={{input: {startAdornment: (
                            <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>
                        )}}}
                    />
                    <EmojiPickerToolbar
                        activeCategory={activeCategory}
                        onCategoryClick={categoryId => {
                            setSearch('');
                            setCategoryJump(categoryId);
                        }}
                        skinTone={skinTone}
                        onSkinToneChange={setSkinTone}
                    />
                    {rows.length > 0 ? (
                        <Virtuoso
                            ref={listRef}
                            data={rows}
                            fixedItemHeight={rowHeight}
                            style={{height: 320}}
                            aria-label="Emojis"
                            rangeChanged={({startIndex}) => setActiveCategory(rows[startIndex]?.categoryId)}
                            itemContent={(_, row) => 'name' in row ? (
                                <Typography component="h3" variant="overline" color="text.secondary"
                                            sx={{height: rowHeight, display: 'flex', alignItems: 'center'}}>
                                    {row.name}
                                </Typography>
                            ) : (
                                <Box sx={{display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, height: rowHeight, gap: 0.5}}>
                                    {row.emojis.map(option => (
                                        <IconButton
                                            title={option.name}
                                            key={option.emoji}
                                            aria-label={option.name}
                                            aria-pressed={value === option.emoji}
                                            onClick={() => {
                                                onChange(option.emoji);
                                                handleClose();
                                            }}
                                            sx={{
                                                borderRadius: 1,
                                                height: 40,
                                                fontSize: 24,
                                                bgcolor: value === option.emoji ? 'action.selected' : undefined,
                                                color: 'text.primary',
                                            }}
                                        >
                                            {option.emoji}
                                        </IconButton>
                                    ))}
                                </Box>
                            )}
                        />
                    ) : (
                        <Typography role="status" variant="body2" color="text.secondary" sx={{py: 2}}>
                            No emojis found. Try another search, or paste an emoji into the field.
                        </Typography>
                    )}
                </Stack>
            </Popover>
        </>
    );
}
